import { z } from 'zod';

/**
 * Canonical notification-channel vocabulary for proactive sends.
 *
 * A NEW, dedicated enum — NOT a subset of `ChatChannelSchema` (which carries 9
 * members: whatsapp/email/sms/phone/slack/teams/app/zendesk/sunshine). A clean
 * subset to the 4 notification channels would have to exclude five of those, so
 * a standalone enum is the correct, explicit choice (D-PC-03 / OQ-4).
 *
 * `app` is excluded by construction: it is the secure destination we direct the
 * traveler to, not a delivery channel.
 */
export const NotificationChannelSchema = z.enum(['whatsapp', 'slack', 'teams', 'email']);

export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

/**
 * Default proactive channel when a customer has NO stored preference.
 *
 * WhatsApp is opt-in: it becomes the preferred channel ONLY after the traveler
 * completes onboarding and proves ownership of the number via the phone-OTP
 * verification (which persists `preferredChannel: 'whatsapp'` as the stored
 * preference). Absent that stored preference, the default is email — we never
 * derive WhatsApp from an unverified mirror number (a corporate-registered
 * phone may be a landline, stale, wrong, or a shared line; WhatsApp requires a
 * mobile the traveler controls). Email has no delivery path until the
 * email-integration milestone, so today an unverified traveler degrades to a
 * no-send rather than a message to an unconfirmed number.
 */
export const DEFAULT_NOTIFICATION_CHANNEL = 'email' as const satisfies NotificationChannel;
